import React, { useCallback, useEffect, useRef } from 'react';
import { DataDefinitionRow, IDataDefinition, IDataDefinitionRowCommonProps } from './DataDefinitionRow';
import './DataDefinitionTable.less';
import classNames from 'classnames';
import { useTypePickerSettings } from 'components/inputs/useTypePickerSettings';

export interface IDataDefinitionTableProps extends React.ComponentProps<'table'>, IDataDefinitionRowCommonProps {
  definitions: IDataDefinition[];
  /**
   * Index of selected rom (indexing from 0)
   */
  selectedRowIndex?: number;
  /**
   * Determines whether it is possible to re-oreder rows (move up/move down).
   *
   * @default true
   */
  reorderableRows?: boolean;
  onAddRow?: () => Promise<void>;
}

/**
 * Table Editor allows edit array of Data Definitions (DataDescription, PlaceholderData, ...)
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DataDefinitionTable({
  className,
  definitions,
  showInputColumn,
  selectedRowIndex,
  reorderableRows = true,
  onNameChanged,
  onIsListChanged,
  onTypeChanged,
  onDelete,
  onRowSelected,
  onMoveUp,
  onMoveDown,
  onAddRow,
  onConstantValueChanged,
  onGetEditMappingContextId: onGetValueContextId,
  mappingContext,
  startEditSelectMapping,
  ...otherProps
}: IDataDefinitionTableProps) {
  const nameChangedRef = useRef<Promise<string>>(Promise.resolve(''));
  const rowAddRef = useRef<Promise<void> | null | undefined>(null);

  const settings = useTypePickerSettings();

  const handleAddRow = useCallback(() => {
    // prevent running "addRow" several times
    if (rowAddRef.current == null) {
      rowAddRef.current = onAddRow?.()?.then(() => {
        rowAddRef.current = null;
      });
    }
  }, [onAddRow]);

  const shouldAddNewRow = definitions.length === 0 || !!definitions[definitions.length - 1].DataName;

  useEffect(() => {
    if (shouldAddNewRow) {
      handleAddRow();
    }
  }, [handleAddRow, shouldAddNewRow]);

  const handleRowSelected = useCallback(
    (index: number, definition: IDataDefinition) => {
      onRowSelected?.(index, definition);
    },
    [onRowSelected]
  );

  const handleNameChanged = useCallback(
    (index: number, newName: string) => {
      nameChangedRef.current = onNameChanged(index, newName);
      return nameChangedRef.current;
    },
    [onNameChanged]
  );

  const handleNameBlur = useCallback(
    (index: number) => {
      const definition = definitions[index];
      const isLast = index === definitions.length - 1;

      if (definition != null && !!definition.DataName && isLast) {
        // wait until name changed
        nameChangedRef.current.then(() => {
          handleAddRow();
        });
      }
    },
    [definitions, handleAddRow]
  );

  const isLastEmptyRow = useCallback(
    (definition: IDataDefinition) => {
      const lastItem = definitions[definitions.length - 1];
      return lastItem === definition && !definition.DataName;
    },
    [definitions]
  );

  return (
    <table className={classNames('dp-data-definition-table', className)} {...otherProps}>
      <thead className="dp-data-definition-table__header">
        <tr>
          <th className="dp-data-definition-row__name-column">
            <div>Name</div>
          </th>
          <th className="dp-data-definition-row__type-column">
            <div>Type</div>
          </th>
          <th className="dp-data-definition-row__is-list-column">
            <div>Is List</div>
          </th>
          {showInputColumn && (
            <>
              <th className="dp-data-definition-row__input-column">
                <div>Input</div>
              </th>
              <th className="dp-data-definition-row__edit-column"></th>
            </>
          )}
          <th className="dp-data-definition-row__delete-column"></th>
          {reorderableRows && (
            <>
              <th className="dp-data-definition-row__move-up-column"></th>
              <th className="dp-data-definition-row__move-down-column"></th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {definitions.map((definition, index) => {
          const isFirst = index === 0;
          const isLast = index === definitions.length - 1;
          const canMoveUp = !isFirst && !isLastEmptyRow(definition);
          const nextRow = definitions[index + 1];
          const canMoveDown = !isLast && nextRow != null && !isLastEmptyRow(nextRow);

          return (
            <DataDefinitionRow
              key={definition.Id}
              index={index}
              isFirst={index === 0}
              isLast={index === definitions.length - 1}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
              isSelected={selectedRowIndex === index}
              reorderableRow={reorderableRows}
              definition={definition}
              showInputColumn={showInputColumn}
              mappingContext={mappingContext}
              settings={settings}
              onNameChanged={handleNameChanged}
              onIsListChanged={onIsListChanged}
              onTypeChanged={onTypeChanged}
              onDelete={onDelete}
              onRowSelected={handleRowSelected}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onNameBlur={handleNameBlur}
              onConstantValueChanged={onConstantValueChanged}
              onGetEditMappingContextId={onGetValueContextId}
              startEditSelectMapping={startEditSelectMapping}
            />
          );
        })}
      </tbody>
    </table>
  );
}
